import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Reuse your axios instance
import { hideLoader, showLoader } from "./loaderSlice";
import { showSnackBar } from "./snackSlice";

export const getBathBays = createAsyncThunk(
  "bathBay/getBathBays",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        "/getall/clinic/facilities"
      );
      if (res.status === 200) {
        const bathBays = res.data.data.filter(
          (item) => item.type === "BathBays"
        );
        thunkAPI.dispatch(hideLoader());
        return bathBays;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to fetch Bath Bays", type: "error" })
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// POST create bath bay
export const createBathBay = createAsyncThunk(
  "bathBay/createBathBay",
  async ({ name, type, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/facilites?type=${type}&name=${name}&status=${status}`
      );
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(
          showSnackBar({
            message: "Bath Bay created successfully!",
            type: "success",
          })
        );
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create Bath Bay");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to create Bath Bay", type: "error" })
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// PUT update bath bay
export const updateBathBay = createAsyncThunk(
  "bathBay/updateBathBay",
  async ({ id, name, type, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/facilites/${id}?type=${type}&name=${name}&status=${status}`
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(
          showSnackBar({
            message: "Bath Bay updated successfully!",
            type: "success",
          })
        );
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update Bath Bay");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to update Bath Bay", type: "error" })
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// DELETE bath bay
export const deleteBathBay = createAsyncThunk(
  "bathBay/deleteBathBay",
  async (id, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/facilites/${id}`
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(
          showSnackBar({
            message: "Bath Bay deleted successfully!",
            type: "success",
          })
        );
        return id;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete Bath Bay");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to delete Bath Bay", type: "error" })
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

const bathBaySlice = createSlice({
  name: "bathBay",
  initialState: {
    bathBays: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getBathBays
      .addCase(getBathBays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBathBays.fulfilled, (state, action) => {
        state.loading = false;
        state.bathBays = action.payload;
      })
      .addCase(getBathBays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createBathBay
      .addCase(createBathBay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBathBay.fulfilled, (state, action) => {
        state.loading = false;
        state.bathBays.push(action.payload);
      })
      .addCase(createBathBay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateBathBay
      .addCase(updateBathBay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBathBay.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.bathBays.findIndex(
          (b) => b.facilityId === action.payload.facilityId
        );
        if (index !== -1) {
          state.bathBays[index] = action.payload;
        }
      })
      .addCase(updateBathBay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // deleteBathBay
      .addCase(deleteBathBay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBathBay.fulfilled, (state, action) => {
        state.loading = false;
        state.bathBays = state.bathBays.filter(
          (b) => b.facilityId !== action.payload
        );
      })
      .addCase(deleteBathBay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default bathBaySlice.reducer;
